import { useState, useEffect, useCallback } from 'react';
import { comprehensiveNotificationService } from '@/services/comprehensiveNotificationService';
import { useUnifiedUser } from '@/hooks/useUnifiedUser';
import { EnhancedNotification } from '@/types/enhancedNotifications';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const useComprehensiveNotifications = () => {
  const [notifications, setNotifications] = useState<EnhancedNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const { user } = useUnifiedUser();

  const loadNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    try {
      console.log('🔄 Loading comprehensive notifications for:', user.username);
      
      const [notificationsList, count] = await Promise.all([
        comprehensiveNotificationService.getNotifications(user),
        comprehensiveNotificationService.getUnreadCount(user)
      ]);

      setNotifications(notificationsList);
      setUnreadCount(count);
      
      console.log(`📬 Loaded ${notificationsList.length} notifications, ${count} unread`);
    } catch (error) {
      console.error('❌ Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const markAsRead = useCallback(async (notificationId: string): Promise<boolean> => {
    const success = await comprehensiveNotificationService.markAsRead(notificationId);
    if (success) {
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    return success;
  }, []);

  const markAllAsRead = useCallback(async (): Promise<boolean> => {
    if (!user) return false;
    
    const success = await comprehensiveNotificationService.markAllAsRead(user);
    if (success) {
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    }
    return success;
  }, [user]);

  // Supabase Realtime subscription for instant notification updates
  useEffect(() => {
    if (!user) return;

    console.log('🔔 Setting up realtime subscription for notifications');
    
    const channel = supabase
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen for INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'notifications'
        },
        (payload) => {
          console.log('📬 Realtime notification change detected:', payload.eventType, payload);
          
          // Check if this notification is for the current user
          const newRecord = payload.new as any;
          const oldRecord = payload.old as any;
          
          // Determine if this affects the current user
          const affectsUser = 
            (newRecord && newRecord.user_id === user.id && newRecord.user_type === user.userType) ||
            (oldRecord && oldRecord.user_id === user.id && oldRecord.user_type === user.userType);
          
          if (affectsUser) {
            console.log('🔔 Notification change affects current user, reloading...');
            
            // Show toast for new notifications
            if (payload.eventType === 'INSERT' && newRecord) {
              const notification = newRecord as EnhancedNotification;
              
              let toastMessage = notification.title || 'New notification';
              
              switch (notification.priority) {
                case 'urgent':
                  toast.error(toastMessage, {
                    description: notification.message,
                    duration: 8000,
                  });
                  break;
                case 'high':
                  toast.warning(toastMessage, {
                    description: notification.message,
                    duration: 6000,
                  });
                  break;
                default:
                  toast.info(toastMessage, {
                    description: notification.message,
                    duration: 4000,
                  });
                  break;
              }
            }
            
            // Reload notifications
            loadNotifications();
          }
        }
      )
      .subscribe((status) => {
        console.log('📡 Realtime subscription status:', status);
      });

    return () => {
      console.log('🔌 Cleaning up realtime subscription');
      supabase.removeChannel(channel);
    };
  }, [user, loadNotifications]);

  // Event listener for local events (same-tab updates)
  useEffect(() => {
    if (!user) return;

    let debounceTimer: NodeJS.Timeout;

    const handleNotificationEvent = (event: CustomEvent) => {
      console.log('🔔 Comprehensive notification event received:', event.type, event.detail);
      
      // Show toast for new notifications
      if (event.type === 'enhanced-notification' && event.detail) {
        const notification = event.detail as EnhancedNotification;
        
        // Only show toast if it's for the current user
        if (notification.user_id === user.id && notification.user_type === user.userType) {
          let toastMessage = notification.title;
          
          switch (notification.priority) {
            case 'urgent':
              toast.error(toastMessage, {
                description: notification.message,
                duration: 8000,
              });
              break;
            case 'high':
              toast.warning(toastMessage, {
                description: notification.message,
                duration: 6000,
              });
              break;
            default:
              toast.info(toastMessage, {
                description: notification.message,
                duration: 4000,
              });
              break;
          }
        }
      }

      // Debounce reload to prevent spam
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        loadNotifications();
      }, 500);
    };

    // Listen for all notification events
    const eventTypes = [
      'enhanced-notification',
      'enhanced-notification-update',
      'unified-notification',
      'unified-notification-update',
      'notification-update',
      'notification-bell-update',
      'notification-manager-update'
    ];

    eventTypes.forEach(eventType => {
      window.addEventListener(eventType, handleNotificationEvent as EventListener);
      document.addEventListener(eventType, handleNotificationEvent as EventListener);
    });

    // Storage change listener for cross-tab sync
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'user_notifications') {
        console.log('💾 Notification storage changed, reloading');
        loadNotifications();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Initial load
    loadNotifications();

    // Periodic check every 60 seconds (backup for realtime)
    const intervalId = setInterval(loadNotifications, 60000);

    return () => {
      eventTypes.forEach(eventType => {
        window.removeEventListener(eventType, handleNotificationEvent as EventListener);
        document.removeEventListener(eventType, handleNotificationEvent as EventListener);
      });
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
      clearTimeout(debounceTimer);
    };
  }, [user, loadNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    loadNotifications
  };
};